/**
 * Loans Service
 * Handles all loan-related APIs (Home Loan, Personal Loan, Gold Loan, Business Loans)
 */

import { API_PATHS } from "../constants/api-paths";
import { encryptedRequest } from "../core/api.client";
import type { IResponse } from "../types/api.types";

/**
 * ======================
 * HOME LOAN (HL) TYPES
 * ======================
 */

export interface IHlInsertRequest {
  mobileNo: string;
  journeyName: string;
  icorsofferId?: string;
  loanAmount?: string;
  loanTenure?: string;
  EMI?: string;
  ROI?: string;
  loanVariant?: string;
  appId?: string;
  appVersion?: string;
  offerId?: string;
  offerIdBhflHlFresh?: string;
  offerIdAfhoFresh?: string;
  offerIdBhflHlBt?: string;
  offerIdAfhoBt?: string;
  offerIdSmelapFresh?: string;
  offerIdSmelapBt?: string;
  offerIdBhflLapFresh?: string;
  offerIdBhflLapBt?: string;
  pdpEntry?: string;
  pdpUrl?: string;
  employmentType?: string;
  sourcingCategory?: string;
  offerSource?: string;
  offerROI?: string;
  offerAmount?: string;
  offerTenor?: string;
  offerName?: string;
  offerID?: string;
  customerConsent?: string;
  grossTurnover?: string;
  sourcingChannel?: string;
  sourceSystem?: string;
}

export interface IHlInsertResponse {
  id: string;
  description?: string;
  statusCode?: string;
  eventCode?: string;
  offerId?: string;
  approvedLoanAmount?: string;
  txId?: string;
  ROI?: string;
  journeyName?: string;
  leadAllocation?: string;
}

/**
 * ======================
 * PERSONAL LOAN (PL) TYPES
 * ======================
 */

export interface IAppDetails {
  pan: string;
  dob: string;
  employmentType?: string;
  fullName: string;
  gender: string;
  kycDtls: {
    address_pin: string;
    address_city: string;
  };
  gstInNo: string;
  monthlyIncome?: string;
}

export interface IPlInsertRequest {
  mobileNo: string;
  journeyName: string;
  processType?: string;
  processSegmentation?: string;
  sourcingBranchId?: string;
  requestedLoanAmt: string;
  loanVariant: string;
  requestedLoanTenor: string;
  hybridTenor?: string;
  dropLineTenor?: string;
  hybridEMIAmt?: string;
  droplineEMIAmt?: string;
  emiAmt?: string;
  requestedIRR: string;
  requestedROI: string;
  callbackRequested: boolean;
  offerId?: string;
  offerName?: string;
  loanType?: string;
  mcpFlag?: string;
  revisedOfferAmt?: string;
  tncFlag?: string;
  srcChannelCategory?: string;
  pqOfferRoiAnnual?: string;
  offerProduct?: string;
  pwaVersion?: string;
  nativeVersion?: string;
  requestSource?: string;
  srcChannel?: string;
  apiTimeout?: string;
  appDtls?: IAppDetails[];
  appVersion?: string;
  cta?: string;
  utmSource?: string;
  utmContent?: string;
  utmCampaign?: string;
  utmMedium?: string;
  utmTerm?: string;
  sourceUrl?: string;
}

export interface IPlInsertResponse {
  id: string;
  description?: string;
  statusCode?: string;
  eventCode?: string;
  offerId?: string;
  txId?: string;
  journeyName?: string;
}

/**
 * ======================
 * GOLD LOAN (GL) TYPES
 * ======================
 */

export interface IGlInsertRequest {
  mobileNo: string;
  journeyName: string;
  loanAmount?: string;
  loanTenure?: string;
  EMI?: string;
  ROI?: string;
  offerId?: string;
  offerName?: string;
  appVersion?: string;
  ornaments?: string;
  goldWeight?: string;
  goldRate?: string;
}

export interface IGlInsertResponse {
  id: string;
  description?: string;
  statusCode?: string;
  eventCode?: string;
  offerId?: string;
  txId?: string;
  journeyName?: string;
}

/**
 * ======================
 * BUSINESS LOANS TYPES
 * ======================
 */

export interface ILapInsertRequest {
  mobileNo: string;
  journeyName: string;
  loanAmount?: string;
  loanTenure?: string;
  EMI?: string;
  ROI?: string;
  offerId?: string;
  propertyType?: string;
  employmentType?: string;
}

export interface ILapInsertResponse {
  id: string;
  description?: string;
  statusCode?: string;
  eventCode?: string;
}

export interface IBlInsertRequest {
  mobileNo: string;
  journeyName: string;
  loanAmount?: string;
  loanTenure?: string;
  EMI?: string;
  ROI?: string;
  offerId?: string;
  businessType?: string;
  grossTurnover?: string;
}

export interface IBlInsertResponse {
  id: string;
  description?: string;
  statusCode?: string;
  eventCode?: string;
}

/**
 * ======================
 * LOAN SUMMARY TYPES
 * ======================
 */

export interface ILoanSummaryRequest {
  mobileNo: string;
}

export interface ILoanDetail {
  loanType: string;
  loanAmount: string;
  emi: string;
  nextEmiDate: string;
  outstandingAmount: string;
  lan: string;
  status: string;
}

export interface ILoanSummaryResponse {
  loans: ILoanDetail[];
  totalOutstanding: string;
  totalEmi: string;
}

/**
 * ======================
 * HOME LOAN FUNCTIONS
 * ======================
 */

/**
 * Insert Home Loan Application
 * Creates a new home loan application
 */
export const hlInsert = async (data: IHlInsertRequest): Promise<IResponse<IHlInsertResponse>> => {
  return encryptedRequest<IHlInsertResponse, IHlInsertRequest>({
    method: "POST",
    url: API_PATHS.hlInsert,
    data,
  });
};

/**
 * ======================
 * PERSONAL LOAN FUNCTIONS
 * ======================
 */

/**
 * Insert Personal Loan Application
 * Creates a new personal loan application
 */
export const plInsert = async (data: IPlInsertRequest): Promise<IResponse<IPlInsertResponse>> => {
  return encryptedRequest<IPlInsertResponse, IPlInsertRequest>({
    method: "POST",
    url: API_PATHS.plInsert,
    data,
  });
};

/**
 * ======================
 * GOLD LOAN FUNCTIONS
 * ======================
 */

/**
 * Insert Gold Loan Application
 * Creates a new gold loan application
 */
export const glInsert = async (data: IGlInsertRequest): Promise<IResponse<IGlInsertResponse>> => {
  return encryptedRequest<IGlInsertResponse, IGlInsertRequest>({
    method: "POST",
    url: API_PATHS.glInsert,
    data,
  });
};

/**
 * ======================
 * BUSINESS LOANS FUNCTIONS
 * ======================
 */

/**
 * Insert LAP (Loan Against Property) Application
 * Note: Renamed to avoid conflict with lapInsert.service.ts
 */
export const lapInsertLoan = async (
  data: ILapInsertRequest
): Promise<IResponse<ILapInsertResponse>> => {
  return encryptedRequest<ILapInsertResponse, ILapInsertRequest>({
    method: "POST",
    url: API_PATHS.lapInsert,
    data,
  });
};

/**
 * Insert Business Loan Application
 */
export const blInsert = async (data: IBlInsertRequest): Promise<IResponse<IBlInsertResponse>> => {
  return encryptedRequest<IBlInsertResponse, IBlInsertRequest>({
    method: "POST",
    url: API_PATHS.blInsert,
    data,
  });
};

/**
 * ======================
 * LOAN SUMMARY FUNCTIONS
 * ======================
 */

/**
 * Get Loan Summary
 * Fetches all loans for a customer
 */
export const getLoanSummary = async (
  mobileNo: string
): Promise<IResponse<ILoanSummaryResponse>> => {
  return encryptedRequest<ILoanSummaryResponse>({
    method: "POST",
    url: API_PATHS.loanSummary,
    data: { mobileNo },
  });
};

/**
 * Loans Service
 * Exported service object with all loan-related functions
 */
export const loansService = {
  // Home Loan
  hlInsert,

  // Personal Loan
  plInsert,

  // Gold Loan
  glInsert,

  // Business Loans
  lapInsert: lapInsertLoan,
  blInsert,

  // Loan Summary
  getLoanSummary,
};
